import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import {
  useUserListDerived,
  useUserListPageEffects,
  useUserStore,
} from "../hooks";
import UserTable from "../components/UserTable";
import CreateUserDialog from "../components/CreateUserDialog";
import UserViewDialog from "../components/UserViewDialog";
import DeleteUserDialog from "../components/DeleteUserDialog";
import UnlockUserDialog from "../components/UnlockUserDialog";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";
import { Download } from "lucide-react";
import { showToast } from "@/lib/utils/toast";
import ExcelJS from "exceljs";

const UserListContent = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogUser, setViewDialogUser] = useState(null);
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [unlockDialogUser, setUnlockDialogUser] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useUserListPageEffects();

  const { isAdminRole, currentUserId } = useUserListDerived();
  const { canCreateUser, canCreateUserMessage, exportAllUsers } =
    useUserStore();
  const { userRole } = useAuthStore();
  const isAllowedToCreateUser = isAdminRole || canCreateUser;

  const handleExportUsers = async () => {
    try {
      setIsExporting(true);

      const allUsers = await exportAllUsers();

      if (!allUsers || allUsers.length === 0) {
        throw new Error("No users found to export");
      }

      const data = allUsers.map((user) => {
        const useCompanyField =
          (user.role === userRole && user.id === currentUserId) ||
          user.role === "super_admin";

        let businessUnit = "";

        if (useCompanyField) {
          businessUnit = typeof user.company === "string" ? user.company : "";
        } else {
          if (Array.isArray(user.organizationUnitId)) {
            const orgNames = user.organizationUnitId
              .map((org) => org.name || "")
              .filter(Boolean);

            if (
              user.assignedToParentCompany &&
              typeof user.company === "string" &&
              user.company.trim()
            ) {
              orgNames.unshift(user.company);
            }

            businessUnit = orgNames.join(", ");
          } else if (
            user.organizationUnitId &&
            typeof user.organizationUnitId === "object"
          ) {
            businessUnit = user.organizationUnitId.name || "";
          } else {
            businessUnit = user.organizationUnitId || "";
          }
        }

        const formattedRole = (user.role || "")
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

        return {
          displayName: user.displayName || user.name || "",
          email: user.email || "",
          role: formattedRole,
          organization: businessUnit,
        };
      });

      // EXCELJS WORKBOOK
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Users");

      // Columns
      sheet.columns = [
        { header: "Display Name", key: "displayName", width: 30 },
        { header: "Email", key: "email", width: 35 },
        { header: "Role", key: "role", width: 20 },
        { header: "Organization / Company", key: "organization", width: 40 },
      ];

      // Add rows
      sheet.addRows(data);

      // HEADER STYLE (IMPORTANT)
      const headerRow = sheet.getRow(1);

      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF1461A1" },
        };

        cell.font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
        };

        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        cell.border = {
          top: { style: "thin", color: { argb: "FFFFFFFF" } },
          bottom: { style: "thin", color: { argb: "FFFFFFFF" } },
          left: { style: "thin", color: { argb: "FFFFFFFF" } },
          right: { style: "thin", color: { argb: "FFFFFFFF" } },
        };
      });

      // Freeze header
      sheet.views = [{ state: "frozen", ySplit: 1 }];

      // Auto filter (like Excel table)
      sheet.autoFilter = {
        from: "A1",
        to: "D1",
      };

      // =========================
      // EXPORT FILE
      // =========================
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      a.download = `User-List-Export-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      a.click();

      window.URL.revokeObjectURL(url);

      showToast.success(t("User list downloaded successfully"));
    } catch (error) {
      console.error("Export failed:", error);
      showToast.error(t("Failed to download user list"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Users")}
        description={t(
          "Manage users, roles, follow-ups, and onboarding in one place.",
        )}
        icon={Users}
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {!isAdminRole && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full sm:w-auto"
                disabled={isExporting}
                onClick={handleExportUsers}
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? t("Exporting...") : t("Export Users")}
              </Button>
            )}
            <Can
              resource="user_list"
              action="create"
              userPermissions={permissions}
            >
              <Button
                type="button"
                size="sm"
                disabled={!isAllowedToCreateUser}
                className="w-full sm:w-auto"
                onClick={() => setCreateDialogOpen(true)}
              >
                {t("Create User")}
              </Button>
            </Can>

            {!isAdminRole && !canCreateUser && canCreateUserMessage ? (
              <span className="max-w-full text-xs text-destructive sm:max-w-[220px] sm:text-right">
                {canCreateUserMessage}
              </span>
            ) : null}
          </div>
        }
      />

      <Can
        resource="user_list"
        action="view"
        showMessage={true}
        userPermissions={permissions}
      >
        <UserTable
          onViewUser={setViewDialogUser}
          onDeleteUser={setDeleteDialogUser}
          onUnlockUser={setUnlockDialogUser}
        />
      </Can>

      <CreateUserDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
      <UserViewDialog
        user={viewDialogUser}
        onClose={() => setViewDialogUser(null)}
      />
      <DeleteUserDialog
        user={deleteDialogUser}
        onClose={() => setDeleteDialogUser(null)}
      />
      <UnlockUserDialog
        user={unlockDialogUser}
        onClose={() => setUnlockDialogUser(null)}
      />
    </div>
  );
});

const UserListPage = () => <UserListContent />;

export default UserListPage;
